import axios from 'axios';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface EsteiraTagOpcao {
  label: string;
  valor: string;
}

export interface EsteiraTagRolePermitido {
  id: string;
  role: string;
}

export interface EsteiraTagDefinicao {
  id: string;
  esteiraId: string;
  nome: string;
  ordem: number;
  ativo: boolean;
  /** at-126: o valor aparece ao associado como motivo da pendência. Ausente em backend anterior. */
  visivelAoAssociado?: boolean;
  opcoes: EsteiraTagOpcao[];
  rolesPermitidos: EsteiraTagRolePermitido[];
}

export interface EsteiraTagValorItem {
  definicaoId: string;
  esteiraId: string;
  nome: string;
  opcoes: EsteiraTagOpcao[];
  rolesPermitidos: string[];
  valor: string | null;
}

export interface CreateEsteiraTagDefinicaoDto {
  esteiraId: string;
  nome: string;
  ordem?: number;
  rolesPermitidos?: string[];
  opcoes?: { label: string; valor: string; ordem?: number }[];
}

export interface UpdateEsteiraTagDefinicaoDto {
  nome?: string;
  ordem?: number;
  ativo?: boolean;
  visivelAoAssociado?: boolean;
}

// ─── Definições (admin) ────────────────────────────────────────────────────

export async function getEsteiraTagDefinicoes(
  esteiraId?: string,
  includeInactive = false,
): Promise<EsteiraTagDefinicao[]> {
  const response = await axios.get(`${BACKEND_URL}/esteira-tags`, {
    params: { esteiraId, includeInactive: includeInactive || undefined },
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function createEsteiraTagDefinicao(
  dto: CreateEsteiraTagDefinicaoDto,
): Promise<EsteiraTagDefinicao> {
  const response = await axios.post(`${BACKEND_URL}/esteira-tags`, dto, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function updateEsteiraTagDefinicao(
  id: string,
  dto: UpdateEsteiraTagDefinicaoDto,
): Promise<EsteiraTagDefinicao> {
  const response = await axios.patch(`${BACKEND_URL}/esteira-tags/${id}`, dto, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function deleteEsteiraTagDefinicao(id: string): Promise<void> {
  await axios.delete(`${BACKEND_URL}/esteira-tags/${id}`, {
    headers: authHeader(),
    withCredentials: true,
  });
}

export async function addEsteiraTagOpcao(
  definicaoId: string,
  opcao: { label: string; valor: string; ordem?: number },
): Promise<EsteiraTagOpcao> {
  const response = await axios.post(
    `${BACKEND_URL}/esteira-tags/${definicaoId}/opcoes`,
    opcao,
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}

export async function removeEsteiraTagOpcao(definicaoId: string, opcaoId: string): Promise<void> {
  await axios.delete(`${BACKEND_URL}/esteira-tags/${definicaoId}/opcoes/${opcaoId}`, {
    headers: authHeader(),
    withCredentials: true,
  });
}

export async function addEsteiraTagRole(definicaoId: string, role: string): Promise<void> {
  await axios.post(
    `${BACKEND_URL}/esteira-tags/${definicaoId}/roles/${role}`,
    {},
    { headers: authHeader(), withCredentials: true },
  );
}

export async function removeEsteiraTagRole(definicaoId: string, role: string): Promise<void> {
  await axios.delete(`${BACKEND_URL}/esteira-tags/${definicaoId}/roles/${role}`, {
    headers: authHeader(),
    withCredentials: true,
  });
}

// ─── Valores do carro ──────────────────────────────────────────────────────

/**
 * at-134: com `esteiraId`, o backend devolve só as tags daquela esteira. Sem ele, todas as que o
 * perfil vê — e cada esteira de fluxo tem a sua "Motivo da pendência", com o mesmo nome.
 */
export async function getCarTagValores(carId: string, esteiraId?: string): Promise<EsteiraTagValorItem[]> {
  const response = await axios.get(
    `${BACKEND_URL}/esteira-tags/cars/${carId}/valores`,
    { headers: authHeader(), withCredentials: true, params: esteiraId ? { esteiraId } : undefined },
  );
  return response.data;
}

export async function upsertCarTagValores(
  carId: string,
  valores: { definicaoId: string; valor?: string | null }[],
): Promise<EsteiraTagValorItem[]> {
  const response = await axios.put(
    `${BACKEND_URL}/esteira-tags/cars/${carId}/valores`,
    { valores },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}
